import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import LeadForm from '@/components/shared/LeadForm';
import { LEADERSHIP_TEAM, FOUNDER_TRIBUTE, TIMELINE, PLACEHOLDERS } from '@/lib/constants';

function FadeIn({ children, delay = 0 }) {
  const { ref, inView } = useInView({ threshold: 0.15, triggerOnce: true });
  return <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay, ease: 'easeOut' }}>{children}</motion.div>;
}

export default function AboutPage() {
  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="bg-[#1A1008] py-20 lg:py-32 relative">
        <div className="noise-overlay absolute inset-0 pointer-events-none" />
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <p className="text-[13px] font-medium text-[#E8642C] tracking-[0.2em] uppercase mb-6">About Us</p>
            <h1 className="text-[clamp(2.5rem,5.5vw,5rem)] font-bold text-white leading-[0.95] tracking-[-0.02em] mb-6">
              18 years. 739+ organisations.<br/><span className="text-[#E8642C]">One mission.</span>
            </h1>
            <p className="text-lg text-white/45 leading-[1.7] max-w-lg">
              We exist because we believe organisations can be built better. More human, more strategic, more resilient.
            </p>
          </motion.div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-24 lg:py-32 bg-[#FAF5EF]" id="story">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <FadeIn>
            <p className="text-[13px] font-medium text-[#E8642C] tracking-[0.2em] uppercase mb-5">Our Journey</p>
            <h2 className="text-4xl font-bold text-[#1A1008] leading-[1.1] tracking-tight mb-16">Our Story</h2>
          </FadeIn>

          <div className="relative" data-testid="about-timeline">
            <div className="absolute left-4 lg:left-8 top-0 bottom-0 w-px bg-[#F5D0A9]" />
            <div className="space-y-0">
              {TIMELINE.map((item, i) => (
                <TimelineItem key={item.year} item={item} index={i} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER TRIBUTE */}
      <section className="py-24 lg:py-32 bg-gradient-to-br from-[#1A1008] to-[#2D1810] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute top-20 right-20 w-96 h-96 bg-[#E8642C] rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <FadeIn>
            <div className="grid lg:grid-cols-[1fr,1.2fr] gap-16 items-center">
              {/* Image */}
              <div className="relative">
                <div className="aspect-square rounded-3xl overflow-hidden border-4 border-[#E8642C]/20 shadow-2xl">
                  <img 
                    src={FOUNDER_TRIBUTE.image} 
                    alt={FOUNDER_TRIBUTE.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#E8642C]/10 rounded-3xl backdrop-blur-sm border border-[#E8642C]/20" />
              </div>

              {/* Content */}
              <div>
                <p className="text-[11px] font-semibold text-[#E8642C] tracking-[0.25em] uppercase mb-6">In Memory</p>
                <h2 className="text-4xl lg:text-5xl font-bold mb-4 tracking-tight text-white">{FOUNDER_TRIBUTE.name}</h2>
                <p className="text-xl text-[#E8642C] font-semibold mb-8">{FOUNDER_TRIBUTE.title}</p>
                <p className="text-lg text-white/80 leading-relaxed mb-8">{FOUNDER_TRIBUTE.description}</p>
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                  <p className="text-white/90 italic leading-relaxed">{FOUNDER_TRIBUTE.legacy}</p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* LEADERSHIP TEAM */}
      <section className="py-24 lg:py-32 bg-white" id="team">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="text-[11px] font-semibold text-[#E8642C] tracking-[0.25em] uppercase mb-6">Leadership</p>
              <h2 className="text-4xl lg:text-5xl font-bold text-[#1A1008] mb-6 tracking-tight">The people behind the transformation</h2>
              <p className="text-lg text-[#6B5B50] max-w-[700px] mx-auto">Meet the leadership team driving Anexi Consulting's mission to transform organizations from the inside out.</p>
            </div>
          </FadeIn>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-8" data-testid="leadership-team-grid">
            {LEADERSHIP_TEAM.map((member, i) => (
              <FadeIn key={member.name} delay={i * 0.05}>
                <div className="group">
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-[#FAF5EF] border border-[#3D1C11]/10 mb-4 relative hover:shadow-xl transition-all">
                    {member.image ? (
                      <img src={member.image} alt={member.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#FAF5EF] to-[#FFF7EF]">
                        <div className="w-20 h-20 rounded-full bg-[#E8642C]/10 flex items-center justify-center">
                          <span className="text-2xl font-bold text-[#E8642C]">
                            {member.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  <h3 className="text-base font-bold text-[#1A1008] leading-snug mb-1">{member.name}</h3>
                  <p className="text-sm text-[#E8642C] font-medium mb-2">{member.title}</p>
                  {member.bio && (
                    <p className="text-xs text-[#6B5B50] leading-relaxed">{member.bio}</p>
                  )}
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="py-24 lg:py-32 bg-white" id="approach">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <FadeIn>
            <div className="max-w-3xl mx-auto text-center mb-16">
              <p className="text-[13px] font-medium text-[#E8642C] tracking-[0.2em] uppercase mb-5">Methodology</p>
              <h2 className="text-4xl font-bold text-[#1A1008] leading-[1.1] tracking-tight mb-4">The Wheel of Transformation</h2>
              <p className="text-[15px] text-[#6B5B50] leading-[1.7]">Our integrated methodology for organisational change.</p>
            </div>
          </FadeIn>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Strategy', desc: 'Aligning people strategy with business goals' },
              { label: 'Structure', desc: 'Designing organisations that scale' },
              { label: 'Systems', desc: 'Building performance and reward frameworks' },
              { label: 'Skills', desc: 'Developing leaders and capabilities' },
              { label: 'Culture', desc: 'Measuring and shaping workplace culture' },
              { label: 'Engagement', desc: 'Listening to and acting on employee voice' },
              { label: 'Experience', desc: 'Creating transformative breakthrough moments' },
              { label: 'Technology', desc: 'Integrating AI and digital solutions' },
            ].map((spoke, i) => (
              <FadeIn key={spoke.label} delay={i * 0.03}>
                <div className="bg-[#FAF5EF] rounded-xl border border-[rgba(61,28,17,0.04)] p-5 text-center hover:shadow-md hover:shadow-[#3D1C11]/3 transition-all">
                  <h4 className="text-[14px] font-semibold text-[#1A1008] mb-1">{spoke.label}</h4>
                  <p className="text-[12px] text-[#6B5B50] leading-relaxed">{spoke.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ALLIANCES */}
      <section className="py-24 lg:py-28 bg-[#FAF5EF]" id="alliances">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <FadeIn>
            <p className="text-[13px] font-medium text-[#E8642C] tracking-[0.2em] uppercase mb-5">Partners</p>
            <h2 className="text-3xl font-bold text-[#1A1008] mb-10 tracking-tight">Strategic Alliances</h2>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { name: 'BOL', desc: 'Global Workplace Survey partner for international benchmarking.' },
              { name: 'Outthinker', desc: 'Strategic thinking and innovation methodology partner.' },
              { name: 'Helping Others Succeed', desc: 'Leadership methodology for coaching-based management.' },
              { name: 'WSABLBY', desc: 'Why Should Anyone Be Led By You? leadership framework.' },
            ].map((alliance, i) => (
              <FadeIn key={alliance.name} delay={i * 0.05}>
                <div className="bg-white rounded-2xl border border-[rgba(61,28,17,0.04)] p-6 hover:shadow-lg hover:shadow-[#3D1C11]/3 transition-all">
                  <div className="w-10 h-10 rounded-xl bg-[#F5D0A9]/20 flex items-center justify-center mb-4">
                    <span className="text-[11px] font-bold text-[#3D1C11]">{alliance.name.slice(0, 3).toUpperCase()}</span>
                  </div>
                  <h3 className="text-[14px] font-semibold text-[#1A1008] mb-1">{alliance.name}</h3>
                  <p className="text-[12px] text-[#6B5B50] leading-relaxed">{alliance.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#E8642C] py-20 lg:py-24 relative">
        <div className="noise-overlay absolute inset-0 pointer-events-none" />
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12 text-center">
          <blockquote className="text-2xl lg:text-3xl font-semibold text-white mb-8 max-w-2xl mx-auto leading-[1.3] tracking-tight">
            "The best way to predict the future is to build it."
          </blockquote>
          <Link to="/contact" className="group inline-flex items-center gap-2.5 bg-white text-[#E8642C] px-8 py-4 rounded-full text-[14px] font-medium hover:bg-[#FAF5EF] transition-colors">
            Start a conversation <ArrowUpRight size={15} className="opacity-60 group-hover:opacity-100 transition-opacity" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function TimelineItem({ item, index }) {
  const { ref, inView } = useInView({ threshold: 0.3, triggerOnce: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -10 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.4, delay: 0.05 }}
      className="relative pl-14 lg:pl-20 py-5 group"
    >
      <div className="absolute left-2.5 lg:left-6.5 top-6 w-3 h-3 rounded-full bg-[#E8642C] border-2 border-[#FAF5EF] z-10 group-hover:scale-125 transition-transform" style={{left: index === 0 ? '10px' : undefined}} />
      <div className="flex flex-col lg:flex-row lg:items-baseline lg:gap-8">
        <span className="text-[13px] font-bold text-[#E8642C] tracking-wide tabular-nums mb-1 lg:mb-0 lg:w-16 flex-shrink-0">{item.year}</span>
        <div>
          <h3 className="text-[15px] font-semibold text-[#1A1008] tracking-tight">{item.title}</h3>
          <p className="text-[14px] text-[#6B5B50] mt-1 leading-relaxed">{item.description}</p>
        </div>
      </div>
    </motion.div>
  );
}
